// Kept outside the element: other cart scripts replace the drawer markup at any time.
const cartDiscountState = { draft: '', busy: false, orphans: new Set() };

const sameCode = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// A rejected code may be valid but lose to an automatic discount (combo, free gift) it doesn't combine with.
// Function discounts come back with an empty type, so anything that isn't a code counts.
const isAutoApplied = (application) => !!application && application.type !== 'discount_code';
const hasAutomaticDiscount = (cart) =>
  (cart?.cart_level_discount_applications || []).some(isAutoApplied) ||
  (cart?.items || []).some((item) =>
    (item.line_level_discount_allocations || []).some((allocation) => isAutoApplied(allocation?.discount_application))
  );

const hasKeyboardFocus = (el) => {
  if (el !== document.activeElement) return false;
  try {
    return el.matches(':focus-visible');
  } catch (error) {
    return true;
  }
};

class CartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.input = this.querySelector('.cart-discount__input');
    this.applyButton = this.querySelector('.cart-discount__apply');
    this.applyLabel = this.querySelector('.cart-discount__apply-label');
    this.spinner = this.querySelector('.cart-discount__spinner');
    this.messageEl = this.querySelector('.cart-discount__message');

    this.applyButton?.addEventListener('click', () => this.apply(hasKeyboardFocus(this.applyButton)));
    this.input?.addEventListener('input', () => {
      cartDiscountState.draft = this.input.value;
      this.setMessage('');
    });
    this.input?.addEventListener('keydown', (event) => {
      // Enter would submit the drawer's <form id="cart"> to checkout; keyCode 229 is Safari's IME commit.
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      this.apply(true);
    });

    this.querySelectorAll('.cart-discount__remove').forEach((button) => {
      button.addEventListener('click', () => this.removeCode(button.dataset.code, hasKeyboardFocus(button)));
    });

    if (cartDiscountState.draft && this.input) this.input.value = cartDiscountState.draft;
    if (cartDiscountState.busy) this.setBusy(true);
  }

  async apply(fromKeyboard) {
    const code = (this.input?.value || '').trim();
    if (!code) {
      this.input?.focus();
      return;
    }
    if (cartDiscountState.busy) return;

    this.setBusy(true);
    this.setMessage('');
    let refocus = null;

    try {
      const before = await this.currentCodes();
      const cart = await this.submit([...before.filter((kept) => !sameCode(kept, code)), code], true);
      const verdict = (cart.discount_codes || []).find((entry) => sameCode(entry?.code, code));

      if (!verdict || verdict.applicable === false) {
        // A rejected code stays on the cart and is flagged again at checkout unless it is taken back off.
        await this.submit(before, false).catch(() => {
          if (!before.some((kept) => sameCode(kept, code))) cartDiscountState.orphans.add(code.toLowerCase());
        });
        const message = hasAutomaticDiscount(cart) ? this.dataset.conflictError || this.dataset.error : this.dataset.error;
        this.live().setMessage(message, 'error');
        refocus = this.live().input;
        return;
      }

      cartDiscountState.orphans.delete(code.toLowerCase());
      cartDiscountState.draft = '';
      const fresh = this.render(cart);
      if (!fresh) return;

      const listed = [...fresh.querySelectorAll('.cart-discount__remove')].some((button) =>
        sameCode(button.dataset.code, code)
      );
      // Delayed so the freshly inserted live region exists before its text changes, or it is not announced.
      if (!listed) {
        setTimeout(() => {
          const live = fresh.live();
          live.setMessage(live.dataset.shippingMessage);
        }, 150);
      }
      if (fromKeyboard) refocus = fresh.input;
    } catch (error) {
      this.live().setMessage(this.dataset.requestError, 'error');
      refocus = this.live().input;
    } finally {
      this.live().setBusy(false);
      refocus?.focus();
    }
  }

  async removeCode(code, fromKeyboard) {
    if (cartDiscountState.busy || !code) return;

    this.setBusy(true);
    this.setMessage('');
    let refocus = null;

    try {
      const current = await this.currentCodes();
      const next = current.filter((kept) => !sameCode(kept, code));
      if (next.length === current.length) throw new Error('Code not on cart');

      const fresh = this.render(await this.submit(next, true));
      if (fresh && fromKeyboard) refocus = fresh.input;
    } catch (error) {
      const live = this.live();
      live.setMessage(this.dataset.requestError, 'error');
      if (fromKeyboard) {
        refocus =
          [...live.querySelectorAll('.cart-discount__remove')].find((button) => sameCode(button.dataset.code, code)) ||
          live.input;
      }
    } finally {
      this.live().setBusy(false);
      refocus?.focus();
    }
  }

  // `discount` replaces the whole set and Liquid can't see shipping-only or pending codes, so read /cart.js.
  async currentCodes() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const cart = await response.json();
    if (!Array.isArray(cart.discount_codes)) throw new Error('No discount_codes');

    return cart.discount_codes
      .filter((entry) => entry?.code && !cartDiscountState.orphans.has(entry.code.trim().toLowerCase()))
      .map((entry) => entry.code);
  }

  async submit(codes, withSections) {
    const body = { discount: codes.join(',') };
    const miniCart = document.querySelector('mini-cart');
    if (withSections && miniCart) {
      body.sections = miniCart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const url = window.theme?.routes?.cart_update_url || '/cart/update.js';
    const init = {
      ...(typeof fetchConfig === 'function'
        ? fetchConfig('json')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }),
      body: JSON.stringify(body),
    };

    let response = await fetch(url, init);
    // Shopify answers 409 while another cart mutation (e.g. a gift add) is still running.
    if (response.status === 409) {
      await new Promise((resolve) => setTimeout(resolve, 600));
      response = await fetch(url, init);
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return response.json();
  }

  render(cart) {
    const miniCart = document.querySelector('mini-cart');
    const sections = cart.sections || {};
    const ids = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

    if (!miniCart || ids.some((id) => document.getElementById(id) && !sections[id])) {
      window.location.reload();
      return null;
    }

    try {
      miniCart.renderContents(cart);
    } catch (error) {
      console.error(error);
      window.location.reload();
      return null;
    }

    // Marks any gift request still in flight as stale, so it repaints instead of painting the pre-discount cart.
    window.gwpManager?.commitRender?.();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-discount', cart } }));

    return document.querySelector('cart-discount');
  }

  live() {
    return this.isConnected ? this : document.querySelector('cart-discount') || this;
  }

  setBusy(busy) {
    cartDiscountState.busy = busy;
    this.toggleAttribute('data-busy', busy);
    if (this.applyButton) this.applyButton.disabled = busy;
    if (this.input) this.input.disabled = busy;
    if (this.applyLabel) this.applyLabel.hidden = busy;
    if (this.spinner) this.spinner.hidden = !busy;
    this.querySelectorAll('.cart-discount__remove').forEach((button) => {
      button.disabled = busy;
    });
  }

  setMessage(text, state) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.classList.toggle('cart-discount__message--error', state === 'error');
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
